/**
 * ChildrenRenderer - Renders child elements within a container.
 *
 * This is the core component that all containers use to render their children.
 * It creates a CSS Grid layout and recursively renders child elements.
 *
 * Ported from the original MyVault application.
 */

import React from "react";
import { Box, Typography } from "@mui/joy";
import { useChildElements } from "../../hooks/useCanvasElements";
import type { CanvasElement, InternalGridConfig } from "../../types";
import type { FieldState } from "./CanvasRenderer";

/**
 * Props for ChildrenRenderer
 */
interface ChildrenRendererProps {
  /** All canvas elements */
  elements: CanvasElement[];
  /** ID of the parent container */
  parentId: string;
  /** Panel ID for Tabs/Accordion (filters children to specific panel) */
  panelId?: string;
  /** Number of grid columns (default 12) */
  columns?: number;
  /** Gap between grid cells in pixels (default 8) */
  gap?: number;
  /** Row height in pixels (default 60) */
  rowHeight?: number;
  /** Internal grid config from parent container (overrides defaults) */
  internalGrid?: InternalGridConfig;
  /** Mode: edit or runtime */
  mode: "edit" | "runtime";
  /** Field state map for form values (runtime mode) */
  fieldState?: Record<string, FieldState>;
  /** Render function for each element */
  renderElement: (element: CanvasElement, isNested: boolean) => React.ReactNode;
  /** Show placeholder when empty */
  showEmptyPlaceholder?: boolean;
  /** Custom empty placeholder text */
  emptyPlaceholderText?: string;
}

/**
 * ChildrenRenderer - Renders child elements in a grid layout.
 */
export function ChildrenRenderer({
  elements,
  parentId,
  panelId,
  columns = 12,
  gap = 8,
  rowHeight = 60,
  internalGrid,
  mode,
  renderElement,
  showEmptyPlaceholder = true,
  emptyPlaceholderText,
}: ChildrenRendererProps) {
  // Get filtered and sorted children
  const children = useChildElements(elements, parentId, panelId);

  // Use internal grid config if provided
  const effectiveColumns = internalGrid?.columns ?? columns;
  const effectiveGap = internalGrid?.gap ?? gap;
  const effectiveRowHeight = internalGrid?.rowHeight ?? rowHeight;

  // Show empty state
  if (children.length === 0) {
    if (!showEmptyPlaceholder) return null;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: effectiveRowHeight * 2,
          border: mode === "edit" ? "2px dashed" : "none",
          borderColor: "neutral.300",
          borderRadius: "sm",
          bgcolor: mode === "edit" ? "background.level1" : "transparent",
          p: 2,
        }}
      >
        <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
          {emptyPlaceholderText ||
            (mode === "edit" ? "Drop elements here" : "No content")}
        </Typography>
      </Box>
    );
  }

  // Calculate max row for minimum height
  const maxRow = children.reduce(
    (max, el) =>
      Math.max(max, (el.layout?.gridRow || 1) + (el.layout?.rowSpan || 1) - 1),
    0,
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${effectiveColumns}, 1fr)`,
        gridAutoRows: `minmax(${effectiveRowHeight}px, auto)`,
        gap: `${effectiveGap}px`,
        width: "100%",
        minHeight:
          maxRow > 0
            ? (maxRow + 1) * (effectiveRowHeight + effectiveGap)
            : undefined,
        alignItems: "stretch",
      }}
    >
      {children.map((child) => renderElement(child, true))}
    </Box>
  );
}

export default ChildrenRenderer;
